import {
  CreateTenantCommand,
  TenantConfigDto,
  TenantDto,
  TenantsApi,
  UpdateTenantCommand,
  UpdateTenantReeferContainerMaxUnpluggedTimeCommand,
} from '@admin/app/api'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export class TenantStore extends BaseEntityStore<TenantDto> {
  currentTenantConfig?: TenantConfigDto

  constructor(private api: TenantsApi) {
    super()
    makeObservable(this, {
      currentTenantConfig: observable,

      sortedTenants: computed,
      skipCraneSplit: computed,
      skipVesselTally: computed,
      skipRailTally: computed,
      showUnifiedOperatorPage: computed,
      hasGeneralCargo: computed,
      hasDamageCatalogue: computed,
      skipYardPlanning: computed,
      allowGateInWhenUnitNotOnTerminal: computed,
      allowDirectDelivery: computed,
      allocationRuleWarningThreshold: computed,
      blockWarningThreshold: computed,
      yardWarningThreshold: computed,
    })
  }

  async load() {
    const { data } = await this.api.tenantsGet()
    this.updateStoreItems(data)
  }

  async loadOne(tenantId: number) {
    const { data } = await this.api.tenantsGetOne(tenantId)
    if (data) this.updateStoreItem(data, tenantId)
  }

  async add(createTenantCommand: CreateTenantCommand) {
    const { data } = await this.api.tenantsPost(createTenantCommand)
    this.updateStoreItem(data, data.id)
  }

  async update(updateTenantCommand: UpdateTenantCommand) {
    const { data } = await this.api.tenantsPut(updateTenantCommand)

    this.updateStoreItem(data, data.id)
    this.loadConfigs()
  }

  async updateReeferContainerMaxUnpluggedTime(
    updateTenantReeferContainerMaxUnpluggedTimeCommand: UpdateTenantReeferContainerMaxUnpluggedTimeCommand,
  ) {
    const { data } = await this.api.tenantsUpdateReeferContainerMaxUnpluggedTime(
      updateTenantReeferContainerMaxUnpluggedTimeCommand,
    )

    this.updateStoreItem(data, data.id)
  }

  async delete(id: number) {
    await this.api.tenantsDelete(id)
    this.deleteStoreItem(id)
  }

  async loadConfigs() {
    const { data } = await this.api.tenantsGetCurrentConfigs()

    runInAction(() => {
      this.currentTenantConfig = data
    })
  }

  public getTenantById(id: number): TenantDto | undefined {
    return this.items.find(x => x.id === id)
  }

  public get sortedTenants(): TenantDto[] {
    return this.items.slice().sort((a, b) => a.id - b.id)
  }

  public get skipCraneSplit(): boolean {
    return this.currentTenantConfig?.skipCraneSplit ?? false
  }

  public get skipVesselTally(): boolean {
    return this.currentTenantConfig?.skipVesselTally ?? false
  }

  public get skipRailTally(): boolean {
    return this.currentTenantConfig?.skipRailTally ?? false
  }

  public get showUnifiedOperatorPage(): boolean {
    return this.currentTenantConfig?.showUnifiedOperatorPage ?? false
  }

  public get hasGeneralCargo(): boolean {
    return this.currentTenantConfig?.hasGeneralCargo ?? false
  }

  public get hasDamageCatalogue(): boolean {
    return this.currentTenantConfig?.hasDamageCatalogue ?? false
  }

  public get skipYardPlanning(): boolean {
    return this.currentTenantConfig?.skipYardPlanning ?? false
  }

  public get allowGateInWhenUnitNotOnTerminal(): boolean {
    return this.currentTenantConfig?.allowGateInWhenUnitNotOnTerminal ?? false
  }

  public get allowDirectDelivery(): boolean {
    return this.currentTenantConfig?.allowDirectDelivery ?? false
  }

  public get allocationRuleWarningThreshold(): number | null {
    return this.currentTenantConfig?.allocationRuleWarningThreshold ?? null
  }

  public get blockWarningThreshold(): number | null {
    return this.currentTenantConfig?.blockWarningThreshold ?? null
  }

  public get yardWarningThreshold(): number | null {
    return this.currentTenantConfig?.yardWarningThreshold ?? null
  }
}
